import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './index.module.scss';
import { formatBoldTextHTML } from '../../../../utils/formatBoldText';

// Компонент модального окна для изображения
const ImageModal = ({ isOpen, onClose, imageSrc, imageAlt }) => {
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = React.useCallback(
    (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        backdropFilter: 'blur(8px)',
        animation: 'fadeIn 0.3s ease-out',
      }}
      onClick={handleBackdropClick}>
      <div
        style={{
          position: 'relative',
          width: '90vw',
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'slideIn 0.3s ease-out',
        }}>
        <button
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'rgba(255, 255, 255, 0.9)',
            border: 'none',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            fontSize: '28px',
            fontWeight: 'bold',
            color: '#333',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            zIndex: 100000,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          }}
          onClick={onClose}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 1)';
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.9)';
            e.target.style.transform = 'scale(1)';
          }}>
          ×
        </button>

        <div
          style={{
            width: '100%',
            height: '80%',
            overflow: 'hidden',
            background: 'transparent',
          }}>
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <img
              src={imageSrc}
              alt={imageAlt}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const PublicationMainBlockV2 = ({ blockData, path_to_folder }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState('');
  const [modalImageAlt, setModalImageAlt] = useState('');
  const [playingVideos, setPlayingVideos] = useState({});
  const videoRefs = useRef({});

  const handleImageClick = (imageSrc, imageAlt) => {
    // Отключаем модальное окно на мобильных и планшетных устройствах
    if (window.innerWidth <= 1024) {
      return;
    }
    setModalImageSrc(imageSrc);
    setModalImageAlt(imageAlt);
    setIsModalOpen(true);
  };

  const handlePlayClick = (videoId) => {
    const videoElement = videoRefs.current[videoId];
    if (videoElement) {
      videoElement.play();
      setPlayingVideos((prev) => ({ ...prev, [videoId]: true }));
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const renderContent = (el, index, allContent) => {
    if ((el.cotent_tag === 'title' || el.content_tag === 'title') && el.content_text !== 'none') {
      // Скрываем заголовок "О нас" в десктопной и планшетной версиях
      const isDesktopOrTablet = window.innerWidth > 768;
      const isAboutUsTitle = el.content_text === 'О нас' || el.content_text === 'о нас';

      if (isDesktopOrTablet && isAboutUsTitle) {
        return null;
      }

      const formattedTitle = formatBoldTextHTML(el.content_text);
      return <div className={styles.title} dangerouslySetInnerHTML={{ __html: formattedTitle }} />;
    } else if ((el.cotent_tag === 'description' || el.content_tag === 'description') && el.content_text !== 'none') {
      const formattedDescription = formatBoldTextHTML(el.content_text);
      // Разбиваем на строки и применяем форматирование к каждой
      const lines = formattedDescription.split('\n');
      return (
        <div className={styles.description}>
          {lines.map((line, lineIndex) => (
            <React.Fragment key={lineIndex}>
              <span dangerouslySetInnerHTML={{ __html: line }} />
              {lineIndex < lines.length - 1 && <br />}
            </React.Fragment>
          ))}
        </div>
      );
    } else if (el.cotent_tag === 'img_or_video' || el.content_tag === 'img_or_video') {
      if (el.img_name !== 'none' && el.video_name === 'none') {
        // Только картинка
        const cleanedImgName = el.img_name.split('/').pop().split('\\').pop();
        const imagePath = `${path_to_folder}${cleanedImgName}`;

        // Проверяем, есть ли подпись после этой картинки
        const nextElement = allContent[index + 1];
        const hasSignature =
          nextElement &&
          (nextElement.cotent_tag === 'signature' || nextElement.content_tag === 'signature') &&
          nextElement.content_text !== 'none';

        return (
          <div className={styles.mediaContainer}>
            <div className={styles.imageContainer}>
              <img
                src={imagePath}
                alt='Video thumbnail'
                className={styles.videoImage}
                onClick={() => handleImageClick(imagePath, 'Изображение')}
                style={{ cursor: 'pointer' }}
              />
            </div>
            {hasSignature && (
              <div
                className={styles.signature}
                dangerouslySetInnerHTML={{
                  __html: `(${formatBoldTextHTML(nextElement.content_text)})`,
                }}
              />
            )}
          </div>
        );
      } else if (el.img_name !== 'none' && el.video_name !== 'none') {
        // Видео с превью
        const cleanedImgName = el.img_name.split('/').pop().split('\\').pop();
        const cleanedVideoName = el.video_name.split('/').pop().split('\\').pop();
        const imagePath = `${path_to_folder}${cleanedImgName}`;
        const videoPath = `${path_to_folder}${cleanedVideoName}`;
        const videoId = `video-${index}`;

        // Проверяем, есть ли подпись после этого видео
        const nextElement = allContent[index + 1];
        const hasSignature =
          nextElement &&
          (nextElement.cotent_tag === 'signature' || nextElement.content_tag === 'signature') &&
          nextElement.content_text !== 'none';

        const isPlaying = playingVideos[videoId];

        return (
          <div className={styles.mediaContainer}>
            <div className={styles.videoContainer}>
              {!isPlaying && (
                <img src={imagePath} alt='Video poster' className={styles.videoPoster} />
              )}
              <video
                ref={(el) => (videoRefs.current[videoId] = el)}
                className={styles.video}
                controls={isPlaying}
                onPlay={() => setPlayingVideos((prev) => ({ ...prev, [videoId]: true }))}>
                <source src={videoPath} type='video/mp4' />
                Ваш браузер не поддерживает видео.
              </video>
              {!isPlaying && (
                <button
                  className={styles.playButton}
                  onClick={() => handlePlayClick(videoId)}
                  aria-label='Воспроизвести видео'>
                  <svg width='80' height='80' viewBox='0 0 80 80' fill='none'>
                    <circle cx='40' cy='40' r='40' fill='lightsteelblue' opacity='0.9' />
                    <path d='M30 20 L30 60 L60 40 Z' fill='white' />
                  </svg>
                </button>
              )}
            </div>
            {hasSignature && (
              <div
                className={styles.signature}
                dangerouslySetInnerHTML={{
                  __html: `(${formatBoldTextHTML(nextElement.content_text)})`,
                }}
              />
            )}
          </div>
        );
      } else if (el.img_name === 'none' && el.video_name !== 'none') {
        // Только видео без превью
        const cleanedVideoName = el.video_name.split('/').pop().split('\\').pop();
        const videoPath = `${path_to_folder}${cleanedVideoName}`;
        const videoId = `video-${index}`;

        // Проверяем, есть ли подпись после этого видео
        const nextElement = allContent[index + 1];
        const hasSignature =
          nextElement &&
          (nextElement.cotent_tag === 'signature' || nextElement.content_tag === 'signature') &&
          nextElement.content_text !== 'none';

        const isPlaying = playingVideos[videoId];

        return (
          <div className={styles.mediaContainer}>
            <div className={styles.videoContainer}>
              <video
                ref={(el) => (videoRefs.current[videoId] = el)}
                className={styles.video}
                controls={isPlaying}
                onPlay={() => setPlayingVideos((prev) => ({ ...prev, [videoId]: true }))}>
                <source src={videoPath} type='video/mp4' />
                Ваш браузер не поддерживает видео.
              </video>
              {!isPlaying && (
                <button
                  className={styles.playButton}
                  onClick={() => handlePlayClick(videoId)}
                  aria-label='Воспроизвести видео'>
                  <svg width='80' height='80' viewBox='0 0 80 80' fill='none'>
                    <circle cx='40' cy='40' r='40' fill='lightsteelblue' opacity='0.9' />
                    <path d='M30 20 L30 60 L60 40 Z' fill='white' />
                  </svg>
                </button>
              )}
            </div>
            {hasSignature && (
              <div
                className={styles.signature}
                dangerouslySetInnerHTML={{
                  __html: `(${formatBoldTextHTML(nextElement.content_text)})`,
                }}
              />
            )}
          </div>
        );
      }
    } else if (el.cotent_tag === 'signature' || el.content_tag === 'signature') {
      // Подпись уже обработана в предыдущем элементе, пропускаем
      return null;
    }
    return null;
  };

  if (!blockData || !blockData.block_content || !Array.isArray(blockData.block_content)) {
    return null;
  }

  return (
    <>
      <div className={styles.publicationMainBlockV2}>
        <div className={styles.contentContainer}>
          {blockData.block_content.map((el, index) => (
            <div key={index} className={styles.contentItem}>
              {renderContent(el, index, blockData.block_content)}
            </div>
          ))}
        </div>
      </div>

      <ImageModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        imageSrc={modalImageSrc}
        imageAlt={modalImageAlt}
      />
    </>
  );
};

export default PublicationMainBlockV2;
